import {
  LISTING_ADDITIONAL_INFO_MATERIAL,
  LISTING_BOAT_EQUIPMENT,
  LISTING_CATEGORY,
  LISTING_COVERS_AND_TARPAULINS,
  LISTING_ENGINE_FUEL_TYPE,
  LISTING_ENGINE_PROPULSION,
  LISTING_EQUIPMENT_TYPES_PER_LISTING_EQUIPMENT_CATEGORY,
  LISTING_GALLEY,
  LISTING_HULL_TYPE,
  LISTING_ON_BOARD_INSTRUMENTS,
  LISTING_TECHNICAL_EQUIPMENT,
  LISTING_TYPES_PER_CATEGORY,
} from '../constants/listing.constants';

/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  /* General listing data */
  {
    scope: 'public',
    label: 'EditListingCategoryForm.category',
    key: 'category',
    schemaType: 'enum',
    enumOptions: Object.values(LISTING_CATEGORY).map(option => ({
      option,
      label: `General.listing.category.${option}`,
    })),
    saveConfig: {
      label: 'EditListingCategoryForm.category',
      isRequired: true,
      requiredMessage: 'EditListingCategoryForm.categoryRequired',
    },
  },
  {
    scope: 'public',
    label: 'EditListingCategoryForm.type',
    key: 'type',
    schemaType: 'enum',
    enumFilterKey: 'category',
    enumOptions: Object.entries(LISTING_TYPES_PER_CATEGORY)
      .map(([category, types]) =>
        types.map(option => ({
          option,
          label: `General.listing.type.${category}.${option}`,
          category,
        }))
      )
      .flat(),
    saveConfig: {
      label: 'EditListingCategoryForm.type',
      isRequired: true,
      requiredMessage: 'EditListingCategoryForm.typeRequired',
    },
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.equipmentType',
    key: 'equipmentType',
    schemaType: 'enum',
    enumFilterKey: 'type',
    enumOptions: Object.entries(LISTING_EQUIPMENT_TYPES_PER_LISTING_EQUIPMENT_CATEGORY)
      .map(([type, types]) =>
        types.map(option => ({
          option,
          label: `General.listing.equipmentType.${option}`,
          type,
        }))
      )
      .flat(),
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.manufacturer',
    key: 'manufacturer',
    schemaType: 'text',
    dontRender: true,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.manufacturer',
    key: 'manufacturerOriginal',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.model',
    key: 'model',
    schemaType: 'text',
    saveConfig: {
      label: 'EditListingDetailsForm.model',
      isRequired: true,
      requiredMessage: 'EditListingDetailsForm.modelRequired',
    },
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.yearBuilt',
    key: 'yearBuilt',
    schemaType: 'long',
    dontRender: true,
    minimum: 1700,
    maximum: 2200,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.hullType',
    key: 'hullType',
    schemaType: 'enum',
    enumOptions: Object.values(LISTING_HULL_TYPE).map(hullType => ({
      option: hullType,
      label: `General.listing.hullType.${hullType}`,
    })),
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.lwl',
    key: 'lwl',
    schemaType: 'long',
  },

  /* Engine fields */
  {
    scope: 'public',
    label: 'EditListingDetailsForm.engineModel',
    key: 'engineModel',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.propulsion',
    key: 'propulsion',
    schemaType: 'enum',
    enumOptions: Object.values(LISTING_ENGINE_PROPULSION).map(propulsion => ({
      option: propulsion,
      label: `General.listing.propulsion.${propulsion}`,
    })),
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.fuelType',
    key: 'fuelType',
    schemaType: 'enum',
    enumOptions: Object.values(LISTING_ENGINE_FUEL_TYPE).map(fuelType => ({
      option: fuelType,
      label: `General.listing.fuelType.${fuelType}`,
    })),
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.numberOfEngines',
    key: 'numberOfEngines',
    schemaType: 'long',
    dontRender: true,
    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.enginePower',
    key: 'enginePower',
    schemaType: 'long',
    dontRender: true,
    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.fuelTank',
    key: 'fuelTank',
    schemaType: 'text',
    saveConfig: {
      placeholderMessage: 'EditListingDetailsForm.fuelTankPlaceholder',
      scope: 'public',
      label: 'EditListingDetailsForm.fuelTank',
      key: 'fuelTank',
      schemaType: 'text',
    },
    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.engineHours',
    key: 'engineHours',
    schemaType: 'text',
    saveConfig: {
      placeholderMessage: 'EditListingDetailsForm.engineHoursPlaceholder',
      scope: 'public',
      label: 'EditListingDetailsForm.engineHours',
      key: 'engineHours',
      schemaType: 'text',
    },
    minimum: 0,
  },

  /* Additional dimensions information */
  {
    scope: 'public',
    label: 'EditListingDetailsForm.beam',
    key: 'beam',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.draught',
    key: 'draught',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.draughtKeelup',
    key: 'draughtKeelup',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.clearance',
    key: 'clearance',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.headroom',
    key: 'headroom',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.loa',
    key: 'loa',
    schemaType: 'text',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.displacement',
    key: 'displacement',
    schemaType: 'long',

    minimum: 0,
  },

  /* Technical Equipment */
  {
    scope: 'public',
    key: 'technicalEquipment',
    label: 'General.listing.technicalEquipment',
    schemaType: 'multi-enum',
    dontRender: true,
    enumOptions: Object.values(LISTING_TECHNICAL_EQUIPMENT).map(techEquipment => ({
      option: techEquipment,
      label: `General.listing.technicalEquipment.${techEquipment}`,
    })),
  },

  /* On-Board Instruments */
  {
    scope: 'public',
    key: 'onBoardInstruments',
    dontRender: true,
    label: 'General.listing.onBoardInstruments',
    schemaType: 'multi-enum',
    enumOptions: Object.values(LISTING_ON_BOARD_INSTRUMENTS).map(instrument => ({
      option: instrument,
      label: `General.listing.onBoardInstruments.${instrument}`,
    })),
  },

  /* Galley */
  {
    scope: 'public',
    key: 'galley',
    dontRender: true,
    label: 'General.listing.galley',
    schemaType: 'multi-enum',
    enumOptions: Object.values(LISTING_GALLEY).map(galley => ({
      option: galley,
      label: `General.listing.galley.${galley}`,
    })),
  },

  /* Covers and Tarpaulins */
  {
    scope: 'public',
    key: 'coversAndTarpaulins',
    dontRender: true,
    label: 'General.listing.coversAndTarpaulins',
    schemaType: 'multi-enum',
    enumOptions: Object.values(LISTING_COVERS_AND_TARPAULINS).map(item => ({
      option: item,
      label: `General.listing.coversAndTarpaulins.${item}`,
    })),
  },

  /* Equipment */
  {
    scope: 'public',
    key: 'equipment',
    label: 'General.listing.equipment',
    schemaType: 'multi-enum',
    enumOptions: Object.values(LISTING_BOAT_EQUIPMENT).map(equipment => ({
      option: equipment,
      label: `General.listing.equipment.${equipment}`,
    })),
  },

  /* Other information */
  {
    scope: 'public',
    label: 'EditListingDetailsForm.hin',
    key: 'hin',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.designer',
    key: 'designer',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.holding',
    key: 'holding',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.freshWater',
    key: 'freshWater',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.cruisingSpeed',
    key: 'cruisingSpeed',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.maxSpeed',
    key: 'maxSpeed',
    schemaType: 'long',

    minimum: 0,
  },

  /* Engine fields */
  {
    scope: 'public',
    label: 'EditListingDetailsForm.serialNumber',
    key: 'serialNumber',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.capacity',
    key: 'capacity',
    schemaType: 'long',

    minimum: 0,
    maximum: 65535,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.strokes',
    key: 'strokes',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.cylinders',
    key: 'cylinders',
    schemaType: 'long',

    minimum: 0,
  },

  /* Trailer fields */
  // dimensions
  {
    scope: 'public',
    label: 'EditListingDetailsForm.length',
    key: 'length',
    schemaType: 'long',
    step: '0.1',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.width',
    key: 'width',
    schemaType: 'long',
    step: '0.1',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.axleWidth',
    key: 'axleWidth',
    schemaType: 'long',
    step: '0.1',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.payload',
    key: 'payload',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.curbWeight',
    key: 'curbWeight',
    schemaType: 'long',

    minimum: 0,
  },

  // aditionalInformation
  {
    scope: 'public',
    label: 'EditListingDetailsForm.axles',
    key: 'axles',
    schemaType: 'long',

    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.braked',
    key: 'braked',
    schemaType: 'boolean',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.material',
    key: 'material',
    schemaType: 'enum',
    enumOptions: Object.values(LISTING_ADDITIONAL_INFO_MATERIAL).map(material => ({
      option: material,
      label: `General.listing.material.${material}`,
    })),
  },

  /* --------------------------------- Mooring -------------------------------- */

  {
    scope: 'public',
    label: 'EditListingDetailsForm.yearUntil',
    key: 'yearUntil',
    schemaType: 'long',
    minimum: 1700,
    maximum: 2200,
  },

  // dimensions
  {
    scope: 'public',
    label: 'EditListingDetailsForm.waterDepth',
    key: 'waterDepth',
    schemaType: 'long',
    minimum: 0,
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.maxBoatDraught',
    key: 'maxBoatDraught',
    schemaType: 'long',
    minimum: 0,
  },

  //additionalInfo
  {
    scope: 'public',
    label: 'EditListingDetailsForm.town',
    key: 'town',
    schemaType: 'text',
  },
  {
    scope: 'public',
    label: 'EditListingDetailsForm.marina',
    key: 'marina',
    schemaType: 'text',
  },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  //   availabilityType: 'oneSeat',
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  // },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
